import { lazy } from 'react'

import { ErrorBoundary } from 'react-error-boundary'
import { createBrowserRouter } from 'react-router'

import { RegionLoadingOverlay } from '@/components/region-loading-overlay'
import { RegionLoadingProvider } from '@/contexts/region-loading'
import { brasilAtendimentoRoutes } from '@/features/atendimento/brasil/routes'
import { chileAtendimentoRoutes } from '@/features/atendimento/chile/routes'
import { brasilEmpreendimentosRoutes } from '@/features/empreendimentos/brasil/routes'
import { chileEmpreendimentosRoutes } from '@/features/empreendimentos/chile/routes'
import { brasilEspelhoDeVendasRoutes } from '@/features/espelho-de-vendas/brasil/routes'
import { chileEspelhoDeVendasRoutes } from '@/features/espelho-de-vendas/chile/routes'
import { chileSimulacaoRapidaRoutes } from '@/features/simulacao-rapida/chile/routes'
import { brasilSimuladorRoutes } from '@/features/simulador/brasil/routes'
import { chileSimuladorRoutes } from '@/features/simulador/chile/routes'
import { ventasApi } from '@/infra'
import { AxiosInterceptor } from '@/infra/http/interceptors/axios-interceptor'
import { RootLayout } from '@/layouts/root-layout'
import { EnvioDocumentosLeadsPage } from '@/pages/envio-documentos-leads'
import { EnvioDocumentosPropostaPage } from '@/pages/envio-documentos-proposta'
import { ErrorPage } from '@/pages/error/error'
import { PropostaPage } from '@/pages/proposta'
import PropostaListarProdutos from '@/pages/proposta-listar-produtos'
import { SimulacaoPage } from '@/pages/simulacao'
import { AuthProvider } from '@/providers'
import { CountryGuard } from '@/routes/guards'

import {
  ConfiguracoesParametrosPageSuspense,
  ConfiguracoesReservaPageSuspense,
  ConsultaLeadsRepresadosPageSuspense,
  DetalhesLeadRepresadoPageSuspense,
} from './pages-suspense'
import { PrivateRoute } from './private'

const LoginPage = lazy(() => import('@/pages/login').then((module) => ({ default: module.LoginPage })))

const HistoricoPage = lazy(() =>
  import('@/pages/historico/index').then((module) => ({ default: module.HistoricoSimulacaoPropostaPage })),
)
const ConsultaLeadsRepresadosPage = lazy(() =>
  import('@/pages/consulta-leads-represados').then((module) => ({ default: module.ConsultaLeadsRepresadosPage })),
)
const CadastroLeadRepresadoPage = lazy(() =>
  import('@/pages/cadastro-lead-represado').then((module) => ({ default: module.CadastroLeadRepresadoPage })),
)
const PropostaAtivaPage = lazy(() =>
  import('@/pages/proposta-ativa').then((module) => ({ default: module.PropostaAtivaPage })),
)
const NotFoundPage = lazy(() =>
  import('@/shared/not-found').then((module) => ({
    default: module.NotFoundPage,
  })),
)
const PropostaDocumentosPage = lazy(() =>
  import('@/pages/proposta-documentos').then((module) => ({ default: module.PropostaDocumentosPage })),
)
const RevisarPage = lazy(() => import('@/pages/proposta-revisar').then((module) => ({ default: module.RevisarPage })))
const DuplicarPropostaResumoPage = lazy(() =>
  import('@/pages/proposta-duplicar-resumo').then((module) => ({ default: module.DuplicarPropostaResumoPage })),
)
const DetalhesLeadRepresadoPage = lazy(() =>
  import('@/pages/detalhes-lead-represado').then((module) => ({ default: module.DetalhesLeadRepresadoPage })),
)
const ConfiguracoesPerfilPage = lazy(() =>
  import('@/pages/configuracoes/perfil').then((module) => ({ default: module.ConfiguracoesPerfilPage })),
)
const ConfiguracoesReservaPage = lazy(() =>
  import('@/pages/configuracoes/reserva').then((module) => ({ default: module.ConfiguracoesReservaPage })),
)
const ConfiguracoesParametrosPage = lazy(() =>
  import('@/pages/configuracoes/parametros').then((module) => ({ default: module.ConfiguracoesParametrosPage })),
)
const ConfiguracoesTermosPage = lazy(() =>
  import('@/pages/configuracoes/termos').then((module) => ({ default: module.ConfiguracoesTermosPage })),
)
const ConfiguracoesCoresLotesPage = lazy(() =>
  import('@/pages/configuracoes/cores-lotes').then((module) => ({ default: module.ConfiguracoesCoresLotesPage })),
)

const commonRoutes = [
  {
    path: 'leads-represados',
    children: [
      {
        index: true,
        element: (
          <ConsultaLeadsRepresadosPageSuspense>
            <ConsultaLeadsRepresadosPage />
          </ConsultaLeadsRepresadosPageSuspense>
        ),
      },
      {
        path: 'cadastrar',
        element: <CadastroLeadRepresadoPage />,
      },
      {
        path: ':id',
        element: (
          <DetalhesLeadRepresadoPageSuspense>
            <DetalhesLeadRepresadoPage />
          </DetalhesLeadRepresadoPageSuspense>
        ),
      },
      {
        path: ':id/enviar-documentos',
        element: <EnvioDocumentosLeadsPage />,
      },
    ],
  },
  {
    path: 'simulacao/:id',
    element: <SimulacaoPage />,
  },
  {
    path: 'proposta',
    children: [
      {
        index: true,
        element: <PropostaPage />,
      },
      {
        path: 'selecao-produtos',
        element: <PropostaListarProdutos />,
      },
      {
        path: ':numero',
        element: <PropostaAtivaPage />,
      },
      {
        path: ':numero/documentos',
        element: <PropostaDocumentosPage />,
      },
      {
        path: ':numero/documentos/envio/:pessoaId',
        element: <EnvioDocumentosPropostaPage />,
      },
      {
        path: ':numero/revisar',
        element: <RevisarPage />,
      },
      {
        path: ':numero/duplicar',
        element: <DuplicarPropostaResumoPage />,
      },
    ],
  },
  {
    path: 'historico/simulacoes',
    element: <HistoricoPage />,
  },
  {
    path: 'historico/propostas',
    element: <HistoricoPage />,
  },
  {
    path: 'configuracoes',
    children: [
      {
        path: 'cores-lotes',
        element: <ConfiguracoesCoresLotesPage />,
      },
      {
        path: 'reserva',
        element: (
          <ConfiguracoesReservaPageSuspense>
            <ConfiguracoesReservaPage />
          </ConfiguracoesReservaPageSuspense>
        ),
      },
      {
        path: 'parametros',
        element: (
          <ConfiguracoesParametrosPageSuspense>
            <ConfiguracoesParametrosPage />
          </ConfiguracoesParametrosPageSuspense>
        ),
      },
      {
        path: 'perfil',
        element: <ConfiguracoesPerfilPage />,
      },
      {
        path: 'termos',
        element: <ConfiguracoesTermosPage />,
      },
    ],
  },
]

const chileRoutes = [
  ...chileAtendimentoRoutes,
  ...chileEmpreendimentosRoutes,
  ...chileEspelhoDeVendasRoutes,
  ...chileSimuladorRoutes,
  ...chileSimulacaoRapidaRoutes,
]
const brasilRoutes = [
  ...brasilAtendimentoRoutes,
  ...brasilEmpreendimentosRoutes,
  ...brasilEspelhoDeVendasRoutes,
  ...brasilSimuladorRoutes,
]

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <ErrorBoundary fallbackRender={ErrorPage}>
        <RegionLoadingProvider>
          <AuthProvider>
            <RegionLoadingOverlay />
            <RootLayout />
          </AuthProvider>
        </RegionLoadingProvider>
      </ErrorBoundary>
    ),
    children: [
      {
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: '',
        element: (
          <AxiosInterceptor api={ventasApi}>
            <CountryGuard>
              <PrivateRoute />
            </CountryGuard>
          </AxiosInterceptor>
        ),
        children: [...commonRoutes, ...brasilRoutes, ...chileRoutes],
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
])
